import { z } from '@customerio/hydra-validation';
import type { IconName } from '@customerio/pluma-icons';
import type { DeliveryDetail } from '@/services/db/delivery/entity';
import { capitalize, isEmptyString } from '@/utils/string';

/**
 * Derives the In-App Config panel view-model from a delivery detail — a port of
 * the Ember `cio-delivery-detail/in-app-config` getters. The gist payload
 * (`detail.body`) is parsed leniently: an invalid body degrades to an empty
 * config (blank priority, the default "Modal, Center" display), matching the
 * Ember component, which builds its `messageConfig` off a `{}` fallback.
 *
 * Route rules come from two sources, exactly as Ember does:
 *  - `detail.route_rules` / `detail.excluded_route_rules` — the structured
 *    Included/Excluded rules (the current editor).
 *  - the gist `routeRule{Android,Apple,Web}` regexes — the legacy "Page Rules"
 *    fallback, shown only when there are no structured rules.
 */

const DO_NOT_DISPLAY_REGEX = '^DO_NOT_DISPLAY_IN_APP$';
const OVERLAY_ELEMENT_PREFIX = 'x-gist-';

/** Delivery-priority integers → labels (Ember `MESSAGE_PRIORITY_LABELS`). */
const PRIORITY_LABELS: Record<number, string> = { 1: 'High', 5: 'Medium', 10: 'Low' };

/** Floating overlay element ids → human labels (Ember `OVERLAY_LABEL`). */
const OVERLAY_LABELS: Record<string, string> = {
  'x-gist-floating-top-left': 'Top Left',
  'x-gist-floating-top': 'Top Center',
  'x-gist-floating-top-right': 'Top Right',
  'x-gist-floating-bottom-left': 'Bottom Left',
  'x-gist-floating-bottom': 'Bottom Center',
  'x-gist-floating-bottom-right': 'Bottom Right',
};

/** In-app route-rule platforms → their Pluma icon (Ember `PLATFORM_CONFIGS`). */
const PLATFORM_ICONS: Record<string, IconName> = { web: 'desktop', ios: 'apple', android: 'android' };

function platformIcon(platform: string): IconName {
  return PLATFORM_ICONS[platform] ?? 'desktop';
}

const optionalString = z.string().optional().catch(undefined);

/** The gist SDK config we read for the display + legacy-route-rule labels. */
const GistPropsSchema = z.object({
  position: optionalString,
  elementId: optionalString,
  encodedMessageHtml: optionalString,
  routeRuleAndroid: optionalString,
  routeRuleApple: optionalString,
  routeRuleWeb: optionalString,
});
type GistProps = z.infer<typeof GistPropsSchema>;

const GistPayloadSchema = z.object({
  priority: z.coerce.number().optional().catch(undefined),
  expiry: optionalString,
  properties: z.object({ gist: GistPropsSchema.optional() }).optional().catch(undefined),
});

function parseGistPayload(body: string): z.infer<typeof GistPayloadSchema> {
  let data: unknown = {};
  try {
    data = JSON.parse(body);
  } catch {
    return {};
  }
  const parsed = GistPayloadSchema.safeParse(data);
  return parsed.success ? parsed.data : {};
}

/**
 * The visual-editor message HTML blob (base64 of the compressed HTML) the Gist
 * SDK stores on a "visual editor" in-app message, or `undefined` for a
 * JSON-editor message. Ember reads `gistPayload.properties.gist.encodedMessageHtml`.
 */
export function inAppEncodedMessageHtml(body: string): string | undefined {
  return parseGistPayload(body).properties?.gist?.encodedMessageHtml;
}

/** The priority label (Ember `inAppMessageDeliveryPriorityLabel`); blank when unknown. */
export function inAppPriorityLabel(priority: number | undefined): string {
  if (priority == null) return '';
  return PRIORITY_LABELS[priority] ?? '';
}

/**
 * The display label — a fused port of Ember `createMessageDisplay` +
 * `inAppMessageDisplayLabel`. No gist config resolves to the default
 * "Modal, Center"; a modal position, floating overlay, or inline element id
 * each produce "<Mode>, <value>".
 */
export function inAppDisplayLabel(gist: GistProps | undefined): string {
  const { position, elementId } = gist ?? {};

  if (position == null && elementId == null) {
    return formatDisplay('Modal', capitalize('center'));
  }
  if (position != null) {
    return formatDisplay('Modal', capitalize(position));
  }
  if (elementId != null && elementId.startsWith(OVERLAY_ELEMENT_PREFIX)) {
    return formatDisplay('Overlay', OVERLAY_LABELS[elementId] ?? '');
  }
  return formatDisplay('Inline', elementId ?? '');
}

function formatDisplay(mode: string, valueLabel: string): string {
  return isEmptyString(valueLabel) ? mode : `${mode}, ${valueLabel}`;
}

/** A single route rule, ready to render (Ember `RouteRule`). */
export type ConfigRouteRule = { operator: string; value: string; platform: string; icon: IconName };

const RouteRuleValueSchema = z.object({ operator: z.string(), value: z.string() });

/**
 * One platform's structured rules. The delivery-detail API sends
 * `{ platform: RuleValue[] }` — the shape Ember's `InAppRouteRules` model and
 * `flattenRouteRules` consume — but the generated OpenAPI type wrongly wraps
 * each platform's rules in `{ Rules: [...] }`, so we accept either form.
 * TODO(services spec): drop the `.Rules` variant once the spec matches the wire.
 */
const PlatformRulesSchema = z
  .union([
    z.array(RouteRuleValueSchema),
    z.object({ Rules: z.array(RouteRuleValueSchema) }).transform((wrapper) => wrapper.Rules),
  ])
  .catch([]);

const RouteRulesSchema = z.record(z.string(), PlatformRulesSchema).catch({});

/** Flatten the API's per-platform route-rule map into a flat rule list. */
export function flattenApiRouteRules(rules: unknown): ConfigRouteRule[] {
  const parsed = RouteRulesSchema.safeParse(rules);
  if (!parsed.success) return [];
  return Object.entries(parsed.data).flatMap(([platform, platformRules]) =>
    platformRules.map((rule) => ({
      operator: rule.operator,
      value: rule.value,
      platform,
      icon: platformIcon(platform),
    })),
  );
}

/** The legacy gist route rules (Ember `extractRouteRulesFromGist`). */
export function extractGistRouteRules(gist: GistProps | undefined): ConfigRouteRule[] {
  if (gist == null) return [];
  return [
    ...parseRouteRuleRegex('android', gist.routeRuleAndroid),
    ...parseRouteRuleRegex('ios', gist.routeRuleApple),
    ...parseRouteRuleRegex('web', gist.routeRuleWeb),
  ];
}

function parseRouteRuleRegex(platform: string, regexStr: string | undefined): ConfigRouteRule[] {
  if (regexStr == null || regexStr === DO_NOT_DISPLAY_REGEX) return [];
  return extractRuleStrings(regexStr).map((ruleStr) => ({
    operator: ruleStr.includes('.*') ? 'contains' : 'equals',
    value: revertFromRegex(ruleStr),
    platform,
    icon: platformIcon(platform),
  }));
}

function extractRuleStrings(regexStr: string): string[] {
  // Trim the leading `^` / trailing `$`, then split alternations and strip the
  // surrounding capture-group parens off each part.
  const trimmed = regexStr.replace(/^\^/, '').replace(/\$$/, '');
  return trimmed.split('|').map((ruleStr) => ruleStr.replace(/^\(\(?/, '').replace(/\)\)?$/, ''));
}

function revertFromRegex(value: string): string {
  let result = value.replace(/\.\*/g, '*');
  if (result.length > 1) {
    result = result.replace(/^\*/, '').replace(/\*$/, '');
  }
  return result;
}

/** The full In-App Config view-model consumed by `<InAppConfig>`. */
export interface InAppConfigView {
  priority: string;
  display: string;
  expiration: string | undefined;
  included: ConfigRouteRule[];
  excluded: ConfigRouteRule[];
  legacy: ConfigRouteRule[];
  /** Legacy "Page Rules" show only when there are no structured Included/Excluded rules. */
  showLegacy: boolean;
}

export function buildInAppConfig(detail: DeliveryDetail): InAppConfigView {
  const payload = parseGistPayload(detail.body);
  const gist = payload.properties?.gist;
  const included = flattenApiRouteRules(detail.route_rules);
  const excluded = flattenApiRouteRules(detail.excluded_route_rules);
  const legacy = extractGistRouteRules(gist);

  return {
    priority: capitalize(inAppPriorityLabel(payload.priority)),
    display: inAppDisplayLabel(gist),
    expiration: isEmptyString(payload.expiry) ? undefined : payload.expiry,
    included,
    excluded,
    legacy,
    showLegacy: included.length === 0 && excluded.length === 0 && legacy.length > 0,
  };
}
